/**
 * Stockage des jetons — conception COOKIE-FIRST.
 * ==============================================
 *
 * Règle absolue de ce starter : **le refresh token ne doit jamais être lisible par le
 * JavaScript.** Sur le web, tout ce que la page peut lire (localStorage, sessionStorage,
 * une variable), une faille XSS peut l'exfiltrer — et un refresh token vaut 7 jours
 * d'accès au compte, renouvelables par rotation, invisibles dans les logs de login.
 *
 * Ce que fait le back (en place et vérifié) :
 *   le back pose le refresh dans un cookie `HttpOnly` que le navigateur renvoie tout seul
 *   et que le JS ne peut pas lire. Le front n'a alors RIEN à stocker ni à faire expirer :
 *   il appelle `/auth/refresh` avec `credentials: "include"`, et c'est tout.
 *
 * Face à un back qui ne poserait PAS ce cookie, le repli est **la mémoire** — jamais
 * localStorage. Conséquence assumée : un rechargement de page déconnecte. C'est le prix
 * correct ; un « provisoire » en localStorage ne s'enlève jamais.
 *
 *   ┌─────────────────┬──────────────────────┬──────────────────────────────┐
 *   │ Jeton           │ Où                   │ Volé par une XSS ?           │
 *   ├─────────────────┼──────────────────────┼──────────────────────────────┤
 *   │ access (15 min) │ mémoire              │ oui, mais courte durée       │
 *   │ refresh (7 j)   │ cookie HttpOnly      │ NON — inaccessible au JS     │
 *   │ refresh (repli) │ mémoire              │ oui, mais meurt avec l'onglet│
 *   └─────────────────┴──────────────────────┴──────────────────────────────┘
 */

export interface Tokens {
  readonly accessToken: string;
  /** Absent quand le back le gère en cookie `HttpOnly` — c'est le cas nominal. */
  readonly refreshToken?: string | undefined;
}

export interface TokenStore {
  getAccessToken(): string | null;
  /**
   * Refresh token détenu par le front. Renvoie `null` en mode cookie : ce n'est PAS une
   * anomalie — cela veut dire que c'est le navigateur qui le porte.
   */
  getRefreshToken(): string | null;
  setTokens(tokens: Tokens): void;
  clear(): void;
  /** Notifié à chaque changement (connexion, refresh, déconnexion). */
  subscribe(listener: (store: TokenStore) => void): () => void;
}

/**
 * Implémentation par défaut : tout en mémoire, rien de persistant.
 *
 * Compatible cookie-first sans changement : quand le back posera le cookie `HttpOnly`,
 * il suffira de ne plus passer `refreshToken` à `setTokens` — le navigateur prend le
 * relais et `getRefreshToken()` renverra `null`, ce que le flux de refresh gère déjà.
 */
export class InMemoryTokenStore implements TokenStore {
  #accessToken: string | null = null;
  #refreshToken: string | null = null;
  readonly #listeners = new Set<(store: TokenStore) => void>();

  getAccessToken(): string | null {
    return this.#accessToken;
  }

  getRefreshToken(): string | null {
    return this.#refreshToken;
  }

  setTokens(tokens: Tokens): void {
    this.#accessToken = tokens.accessToken;
    this.#refreshToken = tokens.refreshToken ?? null;
    this.#emit();
  }

  clear(): void {
    this.#accessToken = null;
    this.#refreshToken = null;
    this.#emit();
  }

  subscribe(listener: (store: TokenStore) => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    for (const listener of this.#listeners) listener(this);
  }
}

/**
 * ⚠️ Volontairement ABSENT de ce starter : une implémentation `localStorage`.
 *
 * Ce n'est pas un oubli. Si tu es tenté d'en ajouter une pour « garder la session après
 * un F5 », la bonne réponse est de faire poser le cookie `HttpOnly` par le back — pas de
 * déposer un credential de 7 jours à portée du premier script injecté.
 */
